import { Box, Typography, Avatar, Card, CardContent, Stack, Divider } from '@mui/material';
import { LocationOnOutlined as LocationIcon, ArrowForwardOutlined as ArrowIcon } from '@mui/icons-material';
import { navigate } from '../../lib/navigation';
import { brandInitials } from '../../lib/format';
import { platformIconData } from '../../lib/social';
import { avatarColor } from './avatarColor';
import Button from '../../components/Button';
import CollaboratorAvatarGroup from './CollaboratorAvatarGroup';
import VerifiedBadge from './VerifiedBadge';
import NicheBadge from '../../components/NicheBadge';

function CreatorProfileCard({ creator }) {
  const bannerUrl = creator.coverUrl || creator.avatarUrl;
  return (
    <Card
      sx={{
        borderRadius: 4,
        boxShadow: '0 20px 60px rgba(20, 10, 50, 0.12)',
        border: '1px solid rgba(20, 10, 50, 0.06)',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ height: 88, position: 'relative', overflow: 'hidden' }}>
        {bannerUrl ? (
          <Box
            sx={{
              position: 'absolute',
              inset: -8,
              background: `url(${bannerUrl}) center/cover no-repeat`,
              filter: creator.coverUrl ? 'none' : 'blur(10px) brightness(0.75)',
              transform: creator.coverUrl ? 'none' : 'scale(1.15)',
            }}
          />
        ) : (
          <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #4E28B5 0%, #7C5CD6 100%)' }} />
        )}
        <Box sx={{ position: 'absolute', top: 16, right: 16, px: 1.25, py: 0.4, borderRadius: 999, bgcolor: 'rgba(20, 10, 50, 0.55)' }}>
          <Typography variant="caption" sx={{ color: 'white', fontWeight: 600, letterSpacing: '0.03em' }}>
            Example profile
          </Typography>
        </Box>
      </Box>
      <CardContent sx={{ pt: 0, px: { xs: 2.5, sm: 3.5 }, pb: 3.5 }}>
        <Stack direction="row" sx={{ alignItems: 'flex-end', justifyContent: 'space-between', mt: -4.5, mb: 1.5 }}>
          <Avatar
            src={creator.avatarUrl}
            sx={{
              width: 84,
              height: 84,
              fontSize: 30,
              fontWeight: 700,
              bgcolor: avatarColor(creator.name),
              border: '4px solid white',
              boxShadow: '0 4px 14px rgba(20, 10, 50, 0.18)',
            }}
          >
            {brandInitials(creator.name)}
          </Avatar>
        </Stack>

        <Typography variant="h6" fontWeight={700}>
          {creator.name}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 1 }}>
          @{creator.username}
        </Typography>
        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', mb: 1.5 }}>
          <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {creator.location}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 2.5 }}>
          {creator.niches.map((niche) => (
            <NicheBadge key={niche}>{niche}</NicheBadge>
          ))}
        </Stack>

        <Stack direction="row" spacing={3} sx={{ mb: 2.5 }}>
          {creator.stats.map((stat) => (
            <Box key={stat.platform}>
              <Typography variant="h6" fontWeight={700}>
                {stat.value}
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                <Box component="img" src={platformIconData(stat.platform)} alt="" sx={{ width: 14, height: 14, borderRadius: '3px' }} />
                <Typography variant="caption" color="text.secondary">
                  {stat.platform}
                </Typography>
              </Stack>
            </Box>
          ))}
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body2" fontWeight={600}>
            {creator.verifiedCampaigns} verified campaigns
          </Typography>
          <VerifiedBadge />
        </Stack>

        <Typography variant="overline" color="text.secondary">
          Featured campaigns
        </Typography>
        <Stack spacing={0.75} sx={{ mb: 2 }}>
          {creator.featuredCampaigns.map((campaign) => (
            <Typography key={campaign} variant="body2" fontWeight={600}>
              {campaign}
            </Typography>
          ))}
        </Stack>

        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Frequent collaborators
        </Typography>
        <CollaboratorAvatarGroup collaborators={creator.frequentCollaborators} />

        <Divider sx={{ my: 2.5 }} />

        <Button
          variant="secondary"
          onClick={() => navigate('/example')}
          sx={{ width: '100%', justifyContent: 'space-between' }}
          endIcon={<ArrowIcon fontSize="small" />}
        >
          View full profile
        </Button>
      </CardContent>
    </Card>
  );
}

export default CreatorProfileCard;
